import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom'
import type {
  Article,
  ArticleSummary,
  Block,
  Category,
  Image,
  Page,
  Publication,
} from '@publiqa/shared'
import { ApiError, get, post } from '../lib/api'
import { formatDate } from '../lib/richtext'
import { useFetch } from '../lib/useFetch'
import { Blocks } from './blocks/Blocks'
import { Card, Pill, Premium, SubscribeForm } from './blocks/Journal'
import { PublicProvider, SmartLink, usePublic, usePubliqApi, type NavPage } from './context'
import { DocView } from './DocView'
import { LEGAL_LINKS, LegalRoute } from './Legal'
import { AuthorBox, ContributorRoute, ContributorsRoute, contributorHref } from './Contributors'
import { PublicLayout, useHead } from './Layout'
import { ReaderProvider, useReader } from './reader'
import { ArticleCard, Loading, NotFound, Picture, SearchIcon } from './ui'
import './public.css'
import './magazine.css'

interface PublicationPayload {
  publication: Publication
  hiddenAnchors: string[]
  closedAnchors: Record<string, string>
  closedVia?: string[]
  pages: NavPage[]
  categories: Category[]
}

/**
 * Point d'entrée d'une publication. `slug` vient soit de l'URL (/p/<slug>),
 * soit de la résolution du domaine propre (base = '').
 */
/**
 * Statistiques de visite : une page vue envoyée au serveur à chaque changement
 * de page (rien en aperçu). Le chemin est relatif au site, qu'il soit servi
 * sur son domaine ou sous /p/<slug> ; le site d'origine n'accompagne que la
 * première page.
 */
function useVisitTracking(slug: string, base: string, active: boolean) {
  const { pathname } = useLocation()
  // Dernier chemin envoyé : un même écran n'est compté qu'une fois (rendus
  // répétés, double effet du mode strict en développement).
  const sent = useRef<string | null>(null)
  useEffect(() => {
    if (!active) return
    const path = (base && pathname.startsWith(base) ? pathname.slice(base.length) : pathname) || '/'
    if (sent.current === path) return
    const firstPage = sent.current === null
    sent.current = path
    let referrer = ''
    if (firstPage && document.referrer) {
      try {
        if (new URL(document.referrer).origin !== location.origin) referrer = document.referrer
      } catch {
        /* référent illisible : ignoré */
      }
    }
    const body = JSON.stringify({ path, referrer })
    const url = `/api/v1/public/p/${slug}/hit`
    try {
      if (navigator.sendBeacon?.(url, new Blob([body], { type: 'application/json' }))) return
    } catch {
      /* repli ci-dessous */
    }
    fetch(url, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => undefined)
  }, [slug, base, pathname, active])
}

export function PublicSite({ slug, base }: { slug: string; base: string }) {
  const [params] = useSearchParams()
  const preview = params.get('preview') === '1'
  const { data, error, loading } = useFetch<PublicationPayload>(
    `/public/p/${slug}${preview ? '?preview=1' : ''}`,
  )

  // Domaine propre en place : l'adresse /p/<slug> de la plateforme est
  // désactivée (sauf aperçu, qui a besoin de la session ouverte ici).
  const domain = data?.publication.status === 'published' ? data.publication.domain : null
  const elsewhere = !!domain && base !== '' && !preview && location.hostname !== domain
  useVisitTracking(slug, base, !!data && !elsewhere && !preview)
  if (loading && !data) return <Loading />
  if (error || !data || elsewhere) {
    return (
      <div className="pub tpl-site">
        <main className="pub-notfound container">
          <p className="nf-code">404</p>
          <h1>Publication introuvable</h1>
          <p>
            {error && error.message !== 'Publication introuvable'
              ? error.message
              : 'Elle n’existe pas, ou n’est pas encore en ligne.'}
          </p>
        </main>
      </div>
    )
  }

  return (
    <PublicProvider value={{ ...data, base, preview, inert: false }}>
      <ReaderProvider>
        <PublicLayout>
          <Routes>
            <Route index element={<HomeRoute />} />
            <Route path="actualites" element={<FeedRoute />} />
            <Route path="rubrique/:category" element={<FeedRoute />} />
            <Route path="tag/:tag" element={<FeedRoute />} />
            <Route path="recherche" element={<SearchRoute />} />
            <Route path="article/:article" element={<ArticleRoute />} />
            <Route path="abonnement" element={<SubscribeRoute />} />
            <Route path="abonnement/confirmer" element={<ConfirmRoute />} />
            <Route path="abonnement/desinscription" element={<UnsubscribeRoute />} />
            <Route path="abonnement/connexion" element={<ReaderLoginRoute />} />
            <Route path="contributeurs" element={<ContributorsRoute />} />
            <Route path="contributeurs/:contributor" element={<ContributorRoute />} />
            {LEGAL_LINKS.map((l) => (
              <Route key={l.slug} path={l.slug} element={<LegalRoute kind={l.kind} />} />
            ))}
            <Route path=":page" element={<PageRoute />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PublicLayout>
      </ReaderProvider>
    </PublicProvider>
  )
}

function usePage(slug: string) {
  const path = usePubliqApi()
  return useFetch<{ page: Page }>(path(`/pages/${slug}`))
}

function HomeRoute() {
  const { publication } = usePublic()
  if (publication.kind === 'magazine') return <MagazineHome />
  return <PageRoute home />
}

function PageRoute({ home = false }: { home?: boolean }) {
  const { page: slug = '' } = useParams()
  const { data, loading, error } = usePage(home ? '_home' : slug)
  useHead(home ? undefined : data?.page.title, data?.page.seoDescription)
  if (loading && !data) return <Loading />
  if (error || !data) return <NotFound />
  const page = data.page
  // Une page sans bannière reçoit un titre, sinon elle commencerait sans repère.
  const startsWithHero = page.blocks[0]?.type === 'hero'
  return (
    <>
      {!startsWithHero && !home && (
        <header className="page-title container">
          <h1>{page.title}</h1>
        </header>
      )}
      <Blocks blocks={page.blocks} pageId={page.id} />
    </>
  )
}

// ──────────────────────────────────────────────────────────── journal : une

/**
 * Accueil du journal = sa page d'accueil, composée de blocs (la une, les
 * rubriques, les plus lus, la lettre…). Sans page d'accueil, une une par
 * défaut : la une puis chaque rubrique.
 */
function MagazineHome() {
  const { categories, publication } = usePublic()
  const { data, loading, error } = usePage('_home')
  useHead(undefined, data?.page.seoDescription)
  const fallback = useMemo<Block[]>(
    () => [
      {
        id: 'une',
        type: 'headline',
        layout: 'slider',
        source: 'featured',
        count: 5,
        showFeed: true,
        feedTitle: 'Le fil',
        interval: 7,
      },
      ...categories
        .filter((c) => !c.parentId)
        .map((c): Block => ({
          id: `rubrique-${c.slug}`,
          type: 'section',
          category: c.slug,
          title: '',
          layout: 'tiles',
          count: 4,
          moreLabel: 'Voir tout',
        })),
    ],
    [categories],
  )
  if (loading && !data) return <Loading />
  const page = !error && data ? data.page : null
  return (
    <div className="mg-home">
      <h1 className="sr-only">
        {publication.name}
        {publication.settings.tagline ? ` — ${publication.settings.tagline}` : ''}
      </h1>
      <Blocks blocks={page?.blocks.length ? page.blocks : fallback} pageId={page?.id ?? ''} />
    </div>
  )
}

// ─────────────────────────────────────────────────── liste / rubrique

const PAGE_SIZE = 12

function FeedRoute() {
  const { category: slug, tag } = useParams()
  const { categories, publication } = usePublic()
  const path = usePubliqApi()
  const category = slug ? categories.find((c) => c.slug === slug) : undefined
  const parent = category?.parentId ? categories.find((c) => c.id === category.parentId) : undefined
  const children = category ? categories.filter((c) => c.parentId === category.id) : []
  const [items, setItems] = useState<ArticleSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const missing = !!slug && !category
  const isMag = publication.kind === 'magazine'
  const title = category?.name ?? (tag ? `#${tag}` : isMag ? 'Le fil' : 'Actualités')
  useHead(title, category?.description)

  const load = (offset: number) => {
    setLoading(true)
    get<{ articles: ArticleSummary[]; total: number }>(
      path('/articles', { category: slug, tag, limit: PAGE_SIZE, offset }),
    )
      .then((r) => {
        setItems((prev) => (offset ? [...prev, ...r.articles] : r.articles))
        setTotal(r.total)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    setItems([])
    if (!missing) load(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, tag])

  if (missing) return <NotFound message="Cette rubrique n’existe pas." />

  const more = !loading && items.length < total && (
    <div className="feed-more">
      <button type="button" className="btn btn-ghost" onClick={() => load(items.length)}>
        Charger plus d’articles
      </button>
    </div>
  )

  if (!isMag) {
    return (
      <div className="container feed">
        <header className="feed-head">
          <h1>{title}</h1>
          <p>
            {total} article{total > 1 ? 's' : ''}
          </p>
        </header>
        <div className="grid cols-3">
          {items.map((a) => (
            <ArticleCard key={a.id} article={a} variant="card" />
          ))}
        </div>
        {loading && <Loading />}
        {more}
        {!loading && !items.length && <p className="feed-empty">Aucun article pour le moment.</p>}
      </div>
    )
  }

  const [lead, ...rest] = items
  return (
    <div className="mg-feed" style={{ '--c': category?.color ?? 'var(--accent)' } as CSSProperties}>
      <header className="mg-feed-head">
        <div className="container">
          {parent && (
            <SmartLink href={`/rubrique/${parent.slug}`} className="mg-feed-parent">
              ← {parent.name}
            </SmartLink>
          )}
          <h1>{title}</h1>
          {category?.description && <p className="mg-feed-intro">{category.description}</p>}
          {children.length > 0 && (
            <nav className="mg-chips" aria-label="Sous-rubriques">
              {children.map((c) => (
                <SmartLink key={c.id} href={`/rubrique/${c.slug}`}>
                  {c.name}
                </SmartLink>
              ))}
            </nav>
          )}
          <p className="mg-feed-count">
            {total} article{total > 1 ? 's' : ''}
          </p>
        </div>
      </header>
      <div className="container mg-feed-body">
        {lead && (
          <div className="mg-feed-lead">
            <Card article={lead} variant="cover" eager />
          </div>
        )}
        <div className="mg-grid">
          {rest.map((a) => (
            <Card key={a.id} article={a} variant="tile" />
          ))}
        </div>
        {loading && <Loading />}
        {more}
        {!loading && !items.length && <p className="feed-empty">Aucun article pour le moment.</p>}
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────── recherche

function SearchRoute() {
  const [params, setParams] = useSearchParams()
  const initial = params.get('q') ?? ''
  const [q, setQ] = useState(initial)
  const [query, setQuery] = useState(initial)
  const path = usePubliqApi()
  useHead('Recherche')
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(q.trim())
      const next = new URLSearchParams(params)
      if (q.trim()) next.set('q', q.trim())
      else next.delete('q')
      setParams(next, { replace: true })
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])
  const { data, loading } = useFetch<{ articles: ArticleSummary[]; total: number }>(
    query.length >= 2 ? path('/articles', { q: query, limit: 30 }) : null,
  )
  return (
    <div className="container mg-search">
      <h1>Rechercher</h1>
      <form className="mg-search-form" role="search" onSubmit={(e) => e.preventDefault()}>
        <SearchIcon />
        <input
          type="search"
          value={q}
          autoFocus
          placeholder="Un mot, un nom, un sujet…"
          onChange={(e) => setQ(e.target.value)}
          aria-label="Rechercher dans les articles"
        />
      </form>
      {query.length >= 2 && (
        <p className="mg-search-count" role="status">
          {loading
            ? 'Recherche…'
            : `${data?.total ?? 0} résultat${(data?.total ?? 0) > 1 ? 's' : ''} pour « ${query} »`}
        </p>
      )}
      <div className="mg-search-results">
        {data?.articles.map((a) => (
          <Card key={a.id} article={a} variant="side" />
        ))}
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────── article

function Share({ title }: { title: string }) {
  const [copied, setCopied] = useState(false)
  const url = typeof window !== 'undefined' ? window.location.href.replace(/[?&]preview=1/, '') : ''
  const enc = encodeURIComponent
  const links = [
    { label: 'X', href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}` },
    { label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { label: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
  ]
  const copy = () =>
    void navigator.clipboard?.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  return (
    <div className="mg-share" aria-label="Partager">
      {links.map((l) => (
        <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer">
          {l.label}
        </a>
      ))}
      <a href={`mailto:?subject=${enc(title)}&body=${enc(url)}`}>E-mail</a>
      <button type="button" onClick={copy}>
        {copied ? 'Lien copié ✓' : 'Copier le lien'}
      </button>
    </div>
  )
}

/** Barre de lecture en haut de l'écran (journal). */
function Progress() {
  const { publication } = usePublic()
  const [pct, setPct] = useState(0)
  useEffect(() => {
    const onScroll = () => {
      const el = document.querySelector('.article-body')
      if (!el) return
      const r = el.getBoundingClientRect()
      const total = r.height - window.innerHeight * 0.6
      setPct(Math.min(100, Math.max(0, (-r.top / Math.max(total, 1)) * 100)))
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  // Portail vers <body> : <main> est un conteneur (requêtes de conteneur), qui
  // deviendrait sinon le repère de cette barre « fixe ».
  return createPortal(
    <div
      className="mg-progress"
      style={{ width: `${pct}%`, background: publication.settings.theme.accent }}
      aria-hidden="true"
    />,
    document.body,
  )
}

/** À la place de la suite d'un article réservé : l'offre, et la connexion des abonnés. */
function Paywall() {
  const { publication } = usePublic()
  const { reader } = useReader()
  const p = publication.settings.paywall
  return (
    <aside className="mg-paywall" aria-label="Article réservé aux abonnés">
      <span className="mg-paywall-badge">
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M8 1.5 9.9 5.4l4.3.6-3.1 3 .7 4.3L8 11.3l-3.8 2 .7-4.3-3.1-3 4.3-.6z" />
        </svg>
        Réservé aux abonnés
      </span>
      <h2>{p?.title || 'La suite est réservée aux abonnés'}</h2>
      {reader ? (
        <p>
          Vous êtes connecté·e ({reader.email}), mais votre formule ne donne pas accès aux articles
          réservés
          {reader.premiumUntil ? ` (abonnement échu le ${formatDate(reader.premiumUntil)})` : ''}.
        </p>
      ) : (
        <p>{p?.text || 'Abonnez-vous pour lire la suite de cet article et tous nos contenus.'}</p>
      )}
      <div className="mg-paywall-actions">
        <SmartLink href={p?.ctaHref || '/abonnement'} className="btn btn-primary">
          {p?.ctaLabel || 'Je m’abonne'}
        </SmartLink>
      </div>
      {!reader && (
        <div className="mg-paywall-login">
          <p>Déjà abonné·e ? Recevez un lien de connexion :</p>
          <ReaderLoginForm compact />
        </div>
      )}
    </aside>
  )
}

export function ArticleView({
  article,
  related = [],
  authorPhoto = null,
}: {
  article: Article
  related?: ArticleSummary[]
  /** Photo de la fiche contributeur de l'auteur, à la place de ses initiales. */
  authorPhoto?: Image | null
}) {
  const { publication } = usePublic()
  const isMag = publication.kind === 'magazine'
  const initials = article.author
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
  return (
    <article className={`article${article.locked ? ' is-locked' : ''}`}>
      {isMag && <Progress />}
      <header className="container article-head">
        <div className="mg-card-tags">
          <Pill article={article} />
          {article.kicker && article.category && (
            <span className="article-kicker">{article.kicker}</span>
          )}
          <Premium article={article} />
        </div>
        <h1>{article.title}</h1>
        {article.excerpt && <p className="article-chapo">{article.excerpt}</p>}
        <div className="article-byline">
          {article.author && (
            <span className="author">
              <span className={`avatar${authorPhoto ? ' has-photo' : ''}`} aria-hidden="true">
                {authorPhoto ? <Picture image={authorPhoto} alt="" /> : initials}
              </span>
              Par{' '}
              {isMag ? (
                <SmartLink href={contributorHref(article.author)}>{article.author}</SmartLink>
              ) : (
                article.author
              )}
            </span>
          )}
          <span>
            {article.status === 'published'
              ? `Publié le ${formatDate(article.publishedAt, true)}`
              : 'Brouillon'}
          </span>
          {article.updatedAt &&
            article.publishedAt &&
            new Date(article.updatedAt).getTime() - new Date(article.publishedAt).getTime() >
              3_600_000 && <span>Mis à jour le {formatDate(article.updatedAt, true)}</span>}
          <span>{article.readingMinutes} min de lecture</span>
        </div>
        <Share title={article.title} />
      </header>
      {article.cover && (
        <figure className="container article-cover">
          <Picture image={article.cover} alt={article.title} eager />
          {article.cover.alt && <figcaption>{article.cover.alt}</figcaption>}
        </figure>
      )}
      <div className="container article-body prose">
        <DocView doc={article.body} />
        {article.locked && <Paywall />}
        {article.tags.length > 0 && !article.locked && (
          <ul className="tags" aria-label="Mots-clés">
            {article.tags.map((t) => (
              <li key={t}>
                <SmartLink href={`/tag/${encodeURIComponent(t)}`}>#{t}</SmartLink>
              </li>
            ))}
          </ul>
        )}
        {isMag && article.author && <AuthorBox name={article.author} />}
        {isMag && !article.locked && (
          <aside className="mg-inline-newsletter">
            <h2>Ne manquez rien</h2>
            <p>
              Recevez la lettre de {publication.name} : l’essentiel de l’actualité, gratuitement.
            </p>
            <SubscribeForm compact />
          </aside>
        )}
      </div>
      {related.length > 0 && (
        <aside className="container related" aria-label="À lire aussi">
          <h2 className="section-title">À lire aussi</h2>
          <div className={isMag ? 'mg-grid' : 'grid cols-4'}>
            {related.map((a) =>
              isMag ? (
                <Card key={a.id} article={a} variant="tile" />
              ) : (
                <ArticleCard key={a.id} article={a} variant="card" />
              ),
            )}
          </div>
        </aside>
      )}
    </article>
  )
}

function ArticleRoute() {
  const { article: slug = '' } = useParams()
  const path = usePubliqApi()
  const { reader } = useReader()
  const { data, loading, error, reload } = useFetch<{
    article: Article
    related: ArticleSummary[]
    authorPhoto?: Image | null
  }>(path(`/articles/${slug}`))
  // Connexion d'un abonné : l'article réservé se recharge, en entier cette fois.
  const locked = !!data?.article.locked
  useEffect(() => {
    if (reader?.premium && locked) reload()
  }, [reader, locked, reload])
  const a = data?.article
  useHead(a ? a.seoTitle || a.title : undefined, a ? a.seoDescription || a.excerpt : undefined)
  if (loading && !data) return <Loading />
  if (error || !data) return <NotFound message="Cet article n’existe pas ou n’est plus en ligne." />
  return (
    <ArticleView article={data.article} related={data.related} authorPhoto={data.authorPhoto} />
  )
}

// ─────────────────────────────────────────────────────────── abonnement

export function ReaderLoginForm({ compact = false }: { compact?: boolean }) {
  const { publication, inert } = usePublic()
  const [email, setEmail] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle')
  const [error, setError] = useState('')
  async function submit(e: FormEvent) {
    e.preventDefault()
    if (inert) return
    setState('busy')
    setError('')
    try {
      await post(`/public/p/${publication.slug}/reader/login`, { email })
      setState('sent')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Envoi impossible pour le moment.')
      setState('idle')
    }
  }
  if (state === 'sent')
    return (
      <p className="mg-subscribe-done" role="status">
        Si cette adresse correspond à un abonnement, un lien de connexion vient d’y être envoyé. Il
        est valable 30 minutes.
      </p>
    )
  return (
    <form className={`mg-subscribe${compact ? ' compact' : ''}`} onSubmit={submit}>
      <div className="mg-subscribe-fields">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Adresse e-mail de l’abonnement"
          aria-label="Adresse e-mail de l’abonnement"
          autoComplete="email"
        />
        <button type="submit" className="btn btn-ghost" disabled={state === 'busy'}>
          {state === 'busy' ? 'Envoi…' : 'Recevoir le lien'}
        </button>
      </div>
      {error && (
        <p className="mg-error" role="alert">
          {error}
        </p>
      )}
    </form>
  )
}

function SubscribeRoute() {
  const { publication } = usePublic()
  const { reader, logout } = useReader()
  const p = publication.settings.paywall
  useHead('Abonnement')
  return (
    <div className="container mg-account">
      <header className="mg-account-head">
        <h1>Abonnement</h1>
        <p>Recevez la lettre, abonnez-vous, ou connectez-vous à votre espace abonné.</p>
      </header>
      <div className="mg-account-grid">
        <section className="mg-account-card">
          <h2>La lettre d’information</h2>
          <p>Gratuite, sans publicité. Désinscription en un clic depuis chaque lettre.</p>
          <SubscribeForm askName />
        </section>
        <section className="mg-account-card accent">
          <h2>{p?.title ? 'S’abonner' : 'L’abonnement'}</h2>
          <p>
            {p?.text ||
              'L’abonnement donne accès à tous les articles réservés aux abonnés. Contactez la rédaction pour souscrire.'}
          </p>
          <SmartLink href={p?.ctaHref || '/contact'} className="btn btn-primary">
            {p?.ctaLabel || 'Je m’abonne'}
          </SmartLink>
        </section>
        <section className="mg-account-card">
          <h2>Espace abonné</h2>
          {reader ? (
            <>
              <p>
                Connecté·e en tant que <strong>{reader.email}</strong>.<br />
                Formule :{' '}
                {reader.premium ? 'abonnement — accès à tous les articles' : 'lettre d’information'}
                {reader.premiumUntil &&
                  reader.premium &&
                  `, jusqu’au ${formatDate(reader.premiumUntil)}`}
                .
              </p>
              <button type="button" className="btn btn-ghost" onClick={() => void logout()}>
                Se déconnecter
              </button>
            </>
          ) : (
            <>
              <p>Pas de mot de passe : recevez un lien de connexion par e-mail.</p>
              <ReaderLoginForm />
            </>
          )}
        </section>
      </div>
    </div>
  )
}

/** Page d'un lien reçu par e-mail : envoie le jeton, affiche le résultat. */
function useTokenAction(path: string, auto: boolean) {
  const [params] = useSearchParams()
  const token = params.get('t') ?? ''
  const [state, setState] = useState<'idle' | 'busy' | 'ok' | 'error'>('idle')
  const [message, setMessage] = useState('')
  // Un lien ne sert qu'une fois : l'action ne doit partir qu'une fois, même
  // si l'effet est rejoué (mode strict de React en développement).
  const started = useRef(false)
  const run = () => {
    if (started.current && auto) return
    started.current = true
    if (!token) {
      setState('error')
      setMessage('Lien incomplet.')
      return
    }
    setState('busy')
    post(path, { token })
      .then(() => setState('ok'))
      .catch((e: unknown) => {
        setState('error')
        setMessage(e instanceof ApiError ? e.message : 'Une erreur est survenue.')
      })
  }
  useEffect(() => {
    if (auto) run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return { state, message, run }
}

function Notice({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="container mg-notice">
      <h1>{title}</h1>
      {children}
    </div>
  )
}

function ConfirmRoute() {
  const { publication } = usePublic()
  const { state, message } = useTokenAction(`/public/p/${publication.slug}/subscribe/confirm`, true)
  useHead('Confirmation')
  if (state === 'ok')
    return (
      <Notice title="Inscription confirmée">
        <p>Merci ! Vous recevrez désormais la lettre de {publication.name}.</p>
        <SmartLink href="/" className="btn btn-primary">
          Lire le journal
        </SmartLink>
      </Notice>
    )
  if (state === 'error')
    return (
      <Notice title="Lien invalide">
        <p>{message}</p>
        <SmartLink href="/abonnement" className="btn btn-ghost">
          Recommencer l’inscription
        </SmartLink>
      </Notice>
    )
  return <Loading />
}

/** Désinscription : un clic de confirmation (les logiciels qui pré-ouvrent les liens ne désinscrivent personne). */
function UnsubscribeRoute() {
  const { publication } = usePublic()
  const { state, message, run } = useTokenAction(`/public/p/${publication.slug}/unsubscribe`, false)
  useHead('Désinscription')
  if (state === 'ok')
    return (
      <Notice title="Vous êtes désinscrit·e">
        <p>Vous ne recevrez plus la lettre de {publication.name}. À bientôt peut-être !</p>
      </Notice>
    )
  return (
    <Notice title="Se désinscrire">
      <p>Vous ne recevrez plus la lettre d’information de {publication.name}.</p>
      {state === 'error' && (
        <p className="mg-error" role="alert">
          {message}
        </p>
      )}
      <button type="button" className="btn btn-primary" disabled={state === 'busy'} onClick={run}>
        Confirmer la désinscription
      </button>
    </Notice>
  )
}

function ReaderLoginRoute() {
  const { publication } = usePublic()
  const { reload } = useReader()
  const { state, message } = useTokenAction(`/public/p/${publication.slug}/reader/verify`, true)
  useHead('Connexion')
  useEffect(() => {
    if (state === 'ok') reload()
  }, [state, reload])
  if (state === 'ok')
    return (
      <Notice title="Vous êtes connecté·e">
        <p>Bonne lecture ! Les articles réservés aux abonnés vous sont ouverts.</p>
        <SmartLink href="/" className="btn btn-primary">
          Aller à la une
        </SmartLink>
      </Notice>
    )
  if (state === 'error')
    return (
      <Notice title="Lien expiré">
        <p>{message}</p>
        <ReaderLoginForm />
      </Notice>
    )
  return <Loading />
}
